import {format} from "date-fns-tz";
import {DateRange, TZDate} from "react-day-picker";
import {useFormContext} from "react-hook-form";
import {FiTrash} from "react-icons/fi";

import Button from "@/components/button/Button.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {Divider} from "@/components/divider/Divider.tsx";
import {DatePickerField} from "@/components/form/fields/DatePickerField.tsx";
import {Icon} from "@/components/icon/Icon.tsx";

// The one off scheduler backend validation expects a specific timestamp format,
// this should be fixed but was out of scope for the most recent datepicker changes
const oneOffScheduleBackendTimestampFormatter = (
  date: TZDate | Date | undefined,
  timezone: string | undefined
): string => {
  if (!date) {
    return "";
  }
  return format(date, "yyyy-MM-dd'T'HH:mm:ssXXX", {timeZone: timezone});
};

interface OneOffScheduleRowProps {
  id: string;
  name: string;
  index: number;
  remove: (i: number) => void;
}

export const OneOffScheduleRow = ({id, name, index, remove}: OneOffScheduleRowProps) => {
  const {user} = useCurrentUserContext();
  const {watch, setValue} = useFormContext();
  return (
    <div key={`schedule-entry-${id}`}>
      <div className="row g-3">
        <div className="col-12 col-md">
          <DatePickerField
            label="Start Date"
            name={`${name}.${index}.once_start_date`}
            mode={"split"}
            className="w-100"
            datePickerV3Props={{
              splitRole: "start",
              selected: {
                from: watch(`${name}.${index}.once_start_date`),
                to: watch(`${name}.${index}.once_end_date`) ?? undefined,
              },
              placement: "bottom-start",
              onChange: (date) => {
                const dateRange = date as DateRange;
                if (!dateRange) {
                  setValue(`${name}.${index}.once_start_date`, "", {shouldDirty: true, shouldValidate: true});
                  return;
                }
                setValue(
                  `${name}.${index}.once_start_date`,
                  dateRange.from ? oneOffScheduleBackendTimestampFormatter(dateRange.from, user?.account.timezone) : "",
                  {shouldDirty: true, shouldValidate: true}
                );
              },
            }}
          />
        </div>
        <div className="col-12 col-md">
          <DatePickerField
            label="End Date"
            name={`${name}.${index}.once_end_date`}
            mode={"split"}
            className="w-100"
            datePickerV3Props={{
              splitRole: "end",
              placement: "bottom-start",
              selected: {
                from: watch(`${name}.${index}.once_start_date`),
                to: watch(`${name}.${index}.once_end_date`) ?? undefined,
              },
              onChange: (date) => {
                const dateRange = date as DateRange;
                if (!dateRange) {
                  setValue(`${name}.${index}.once_end_date`, "", {shouldDirty: true, shouldValidate: true});
                  return;
                }
                setValue(
                  `${name}.${index}.once_end_date`,
                  dateRange.to ? oneOffScheduleBackendTimestampFormatter(dateRange.to, user?.account.timezone) : "",
                  {shouldDirty: true, shouldValidate: true}
                );
              },
            }}
          />
        </div>
        <div className="col-12 col-md-1 d-flex align-items-end justify-content-md-center">
          <Button
            variant="ghost"
            size="fitContent"
            onClick={() => {
              remove(index);
            }}
          >
            <Icon icon={FiTrash} className="m-md-2" />
            <span className="d-md-none d-inline-block">Remove Schedule</span>
          </Button>
        </div>
      </div>
      <div>
        <Divider className="my-3" />
      </div>
    </div>
  );
};
